import { useEffect, useMemo, useState } from "react";

import { Navigate, Route, Routes, useLocation } from "react-router-dom";

import CssBaseline from "@mui/material/CssBaseline";
import Icon from "@mui/material/Icon";
import { ThemeProvider } from "@mui/material/styles";

import SoftBox from "components/SoftBox";

import Configurator from "examples/Configurator";

import theme from "assets/theme";
import themeRTL from "assets/theme/theme-rtl";

import createCache from "@emotion/cache";
import { CacheProvider } from "@emotion/react";
import rtlPlugin from "stylis-plugin-rtl";

import { authRoutes, dashboardRoutes } from "routes";

import {
  setMiniSidenav,
  setOpenConfigurator,
  useSoftUIController,
} from "context";
import { NotificationProvider } from "context/NotificationContext";

import DashboardLayout from "layouts/dashboardLayout";
import ProtectedRoute from "components/ProtectedRoute";
import AuthDebug from "components/AuthDebug";
import NotificationStack from "components/NotificationStack";

const renderNestedRoutes = (routeCollection) =>
  routeCollection.map(({ path, element, index, key }) =>
    index ? (
      <Route index element={element} key={key || "index"} />
    ) : (
      <Route path={path} element={element} key={path} />
    )
  );

export default function App() {
  const [controller, dispatch] = useSoftUIController();
  const { miniSidenav, direction, layout, openConfigurator, sidenavColor } =
    controller;
  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const [rtlCache, setRtlCache] = useState(null);
  const { pathname } = useLocation();

  useMemo(() => {
    const cacheRtl = createCache({
      key: "rtl",
      stylisPlugins: [rtlPlugin],
    });

    setRtlCache(cacheRtl);
  }, []);

  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  const handleConfiguratorOpen = () =>
    setOpenConfigurator(dispatch, !openConfigurator);

  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  const configsButton = (
    <SoftBox
      display="flex"
      justifyContent="center"
      alignItems="center"
      width="3.5rem"
      height="3.5rem"
      bgColor="white"
      shadow="sm"
      borderRadius="50%"
      position="fixed"
      right="2rem"
      bottom="2rem"
      zIndex={99}
      color="dark"
      sx={{ cursor: "pointer" }}
      onClick={handleConfiguratorOpen}
    >
      <Icon fontSize="default" color="inherit">
        settings
      </Icon>
    </SoftBox>
  );

  const dashboardRouteElements = renderNestedRoutes(dashboardRoutes);
  const authRouteElements = renderNestedRoutes(authRoutes);

  const routingTree = (
    <Routes>
      <Route
        path="/dashboard/*"
        element={
          <ProtectedRoute>
            <DashboardLayout />
          </ProtectedRoute>
        }
      >
        {dashboardRouteElements}
      </Route>
      {authRouteElements}
      <Route path="/" element={<Navigate to="/sign-in" replace />} />
      <Route path="*" element={<Navigate to="/sign-in" replace />} />
    </Routes>
  );

  const themedApp = (
    <>
      <CssBaseline />
      {layout === "dashboard" && (
        <>
          <Configurator />
          {/* {configsButton} */}
        </>
      )}
      {/* {layout === "vr" && <Configurator />} */}
      {routingTree}
      <NotificationStack />
      {/* Debug component - only shows in development */}
      {/* <AuthDebug /> */}
    </>
  );

  return direction === "rtl" ? (
    <CacheProvider value={rtlCache}>
      <ThemeProvider theme={themeRTL}>{themedApp}</ThemeProvider>
    </CacheProvider>
  ) : (
    <ThemeProvider theme={theme}>{themedApp}</ThemeProvider>
  );
}
